import React, { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  FolderIcon, 
  DocumentIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { apiCall } from '../services/api';
import toast from 'react-hot-toast';

interface FixedUploadDialogProps {
  bucketName: string;
  prefix: string;
  onUploadComplete: () => void;
  permissions: {
    upload: boolean;
    maxObjectSize: number;
  };
}

interface FileItem {
  file: File;
  path: string;
  size: string;
  type: string;
  icon: React.ReactNode;
}

const FixedUploadDialog: React.FC<FixedUploadDialogProps> = ({
  bucketName,
  prefix,
  onUploadComplete,
  permissions
}) => {
  const [selectedFiles, setSelectedFiles] = useState<FileItem[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Helper function to recursively process directory entries from drag and drop
  const processDirectoryEntry = useCallback(async (directoryEntry: FileSystemDirectoryEntry, path = ''): Promise<File[]> => {
    const files: File[] = [];
    
    return new Promise((resolve) => {
      const reader = directoryEntry.createReader();
      
      const readEntries = () => {
        reader.readEntries(async (entries) => {
          if (entries.length === 0) {
            resolve(files);
            return;
          }
          
          for (const entry of entries) {
            const fullPath = path ? `${path}/${entry.name}` : entry.name;
            
            if (entry.isFile) {
              const fileEntry = entry as FileSystemFileEntry;
              const file = await new Promise<File>((resolveFile) => {
                fileEntry.file((file) => {
                  // Add webkitRelativePath to mimic folder input behavior
                  Object.defineProperty(file, 'webkitRelativePath', {
                    value: `${directoryEntry.name}/${fullPath}`,
                    writable: false
                  });
                  resolveFile(file);
                });
              });
              files.push(file);
            } else if (entry.isDirectory) {
              const subFiles = await processDirectoryEntry(entry as FileSystemDirectoryEntry, fullPath);
              files.push(...subFiles);
            }
          }
          
          // Continue reading if there might be more entries
          readEntries();
        });
      };
      
      readEntries();
    });
  }, []);

  const getFileIcon = (file: File): React.ReactNode => {
    if (file.type.startsWith('image/')) {
      return <div className="w-6 h-6 bg-green-100 rounded flex items-center justify-center text-green-600 text-xs font-bold">IMG</div>;
    } else if (file.type.startsWith('video/')) {
      return <div className="w-6 h-6 bg-purple-100 rounded flex items-center justify-center text-purple-600 text-xs font-bold">VID</div>;
    } else if (file.type.startsWith('audio/')) {
      return <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center text-blue-600 text-xs font-bold">AUD</div>;
    } else if (file.type.includes('pdf')) {
      return <div className="w-6 h-6 bg-red-100 rounded flex items-center justify-center text-red-600 text-xs font-bold">PDF</div>;
    } else if (file.type.includes('text') || file.name.endsWith('.txt')) {
      return <div className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center text-gray-600 text-xs font-bold">TXT</div>;
    } else {
      return <DocumentIcon className="w-6 h-6 text-gray-400" />;
    }
  };

  const processFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const processedFiles: FileItem[] = [];
    const oversizedFileNames: string[] = [];

    fileArray.forEach(file => {
      // Check file size if limit is set
      if (permissions.maxObjectSize > 0 && file.size > permissions.maxObjectSize * 1024 * 1024) {
        oversizedFileNames.push(file.name);
        return; // Skip files that are too large
      }

      const path = (file as any).webkitRelativePath || file.name;
      processedFiles.push({
        file,
        path,
        size: formatFileSize(file.size),
        type: file.type || 'Unknown',
        icon: getFileIcon(file)
      });
    });

    // Show toast error for oversized files
    if (oversizedFileNames.length > 0) {
      const maxSize = permissions.maxObjectSize;
      if (oversizedFileNames.length === 1) {
        toast.error(`"${oversizedFileNames[0]}" exceeds the ${maxSize}MB file size limit`);
      } else if (oversizedFileNames.length <= 3) {
        toast.error(`${oversizedFileNames.length} files exceed the ${maxSize}MB limit: ${oversizedFileNames.join(', ')}`);
      } else {
        toast.error(`${oversizedFileNames.length} files exceed the ${maxSize}MB file size limit`);
      }
    }

    setSelectedFiles(prev => [...prev, ...processedFiles]);
  }, [permissions.maxObjectSize]);

  const handleFileSelect = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFolderSelect = useCallback(() => {
    if (folderInputRef.current) {
      folderInputRef.current.click();
    }
  }, []);

  const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
    // Reset input
    event.target.value = '';
  }, [processFiles]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading && permissions.upload) {
      setDragActive(true);
    }
  }, [uploading, permissions.upload]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (!uploading && permissions.upload) {
      const items = e.dataTransfer.items;
      
      if (items && items.length > 0) {
        // Process DataTransferItems to handle folders properly
        const allFiles: File[] = [];
        
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          
          if (item.kind === 'file') {
            const entry = item.webkitGetAsEntry();
            
            if (entry) {
              if (entry.isFile) {
                // Handle individual files
                const file = item.getAsFile();
                if (file) {
                  allFiles.push(file);
                }
              } else if (entry.isDirectory) {
                // Handle folders recursively
                const folderFiles = await processDirectoryEntry(entry as FileSystemDirectoryEntry);
                allFiles.push(...folderFiles);
              }
            }
          }
        }
        
        if (allFiles.length > 0) {
          console.log('Files from drag and drop:', allFiles.map(f => ({
            name: f.name,
            webkitRelativePath: (f as any).webkitRelativePath || 'N/A',
            size: f.size
          })));
          processFiles(allFiles);
        }
      } else {
        // Fallback to regular files if items API is not available
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
          processFiles(files);
        }
      }
    }
  }, [processFiles, uploading, permissions.upload]);

  const removeFile = useCallback((index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearAllFiles = useCallback(() => {
    setSelectedFiles([]);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!permissions.upload || selectedFiles.length === 0) {
      return;
    }

    setUploading(true);
    let uploadedCount = 0;
    let failedCount = 0;
    let totalUploadedSize = 0;
    const uploadedFiles: string[] = [];

    // Calculate total size before upload for audit
    const totalPlannedSize = selectedFiles.reduce((sum, item) => sum + item.file.size, 0);

    for (const fileItem of selectedFiles) {
      try {
        // Handle folder uploads by preserving the webkitRelativePath
        const fileName = (fileItem.file as any).webkitRelativePath || fileItem.file.name;
        const fullPath = prefix + fileName;

        // Skip audit logging for individual files - we'll log once at the end
        const uploadResponse = await apiCall(`/s3/upload/${bucketName}`, {
          method: 'POST',
          body: JSON.stringify({
            fileName: fullPath,
            fileSize: fileItem.file.size,
            skipAudit: true // Tell backend to skip individual audit logging
          })
        });

        const uploadResult = await fetch(uploadResponse.uploadUrl, {
          method: 'PUT',
          body: fileItem.file,
          headers: {
            'Content-Type': fileItem.file.type || 'application/octet-stream'
          }
        });

        if (uploadResult.ok) {
          uploadedCount++;
          totalUploadedSize += fileItem.file.size;
          uploadedFiles.push(fullPath);
        } else {
          failedCount++;
          throw new Error(`Upload failed with status ${uploadResult.status}`);
        }
      } catch (error) {
        failedCount++;
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${fileItem.file.name}`);
      }
    }

    // Log single audit entry for the entire upload operation
    if (uploadedCount > 0) {
      try {
        await apiCall(`/s3/upload-complete/${bucketName}`, {
          method: 'POST',
          body: JSON.stringify({
            fileCount: uploadedCount,
            totalSize: totalUploadedSize,
            folder: prefix || 'root',
            files: uploadedFiles
          })
        });
      } catch (error) {
        console.error('Failed to log upload audit:', error);
      }
    }

    setUploading(false);
    
    if (uploadedCount > 0) {
      toast.success(`Successfully uploaded ${uploadedCount} file(s)`);
      setSelectedFiles([]);
      onUploadComplete();
    }
  }, [selectedFiles, permissions.upload, bucketName, prefix, onUploadComplete]);

  const oversizedFiles = selectedFiles.filter(item => 
    permissions.maxObjectSize > 0 && item.file.size > permissions.maxObjectSize * 1024 * 1024
  );

  if (!permissions.upload) {
    return null;
  }

  return (
    <div className="bg-white shadow-sm rounded-xl overflow-hidden border border-gray-200 mb-6">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CloudArrowUpIcon className="h-6 w-6 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">Upload Files</h3>
            {uploading && (
              <div className="flex items-center text-blue-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                <span className="text-sm font-medium">Uploading...</span>
              </div>
            )}
          </div>
          {selectedFiles.length > 0 && !uploading && (
            <button
              onClick={clearAllFiles}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* File Selection Area */}
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
            dragActive
              ? 'border-blue-400 bg-blue-50 scale-105'
              : uploading
              ? 'border-gray-300 bg-gray-50'
              : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
          }`}
        >
          <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-900 mb-2">
            {dragActive ? 'Drop files here!' : 'Drag & drop files or folders here'}
          </p>
          <p className="text-sm text-gray-500 mb-4">
            or choose files using the buttons below
          </p>
          
          <div className="flex justify-center space-x-4">
            <button
              onClick={handleFileSelect}
              disabled={uploading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <DocumentIcon className="w-4 h-4 mr-2" />
              Select Files
            </button>
            
            <button
              onClick={handleFolderSelect}
              disabled={uploading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FolderIcon className="w-4 h-4 mr-2" />
              Select Folder
            </button>
          </div>

          {permissions.maxObjectSize > 0 && (
            <p className="text-xs text-gray-500 mt-4">
              Maximum file size: {permissions.maxObjectSize} MB
            </p>
          )}
        </div>

        {/* Selected Files List */}
        {selectedFiles.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-900">
                Selected Files ({selectedFiles.length})
              </h4>
              <button
                onClick={handleUpload}
                disabled={uploading || selectedFiles.length === 0}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <CloudArrowUpIcon className="w-4 h-4 mr-2" />
                    Upload ({selectedFiles.length})
                  </>
                )}
              </button>
            </div>
            
            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
              {selectedFiles.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center justify-between p-3 border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {item.icon}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.path}
                      </p>
                      <p className="text-xs text-gray-500">
                        {item.size} • {item.type || 'Unknown type'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    disabled={uploading}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
                  >
                    <XMarkIcon className="h-4 w-4 text-gray-400" />
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Warnings */}
        {oversizedFiles.length > 0 && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
              <p className="text-sm text-yellow-700">
                {oversizedFiles.length} file(s) exceed the {permissions.maxObjectSize} MB limit and will be skipped.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
      <input
        ref={folderInputRef}
        type="file"
        multiple
        {...({ webkitdirectory: '' } as any)}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default FixedUploadDialog;